// src/components/ProtectedRoute.js
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "../firebaseConfig";
import {
  isAuthed,
  getSession,
  onAuthChange,
  isExpired,
  adminLogout,
} from "../services/adminAuth";

/**
 * ProtectedRoute (Supports both Firebase Auth + Local adminAuth)
 * - Firebase: requires claim admin=true or role='admin' or email in whitelist
 * - Local: requires valid local session (adminAuth)
 */
export default function ProtectedRoute({ children }) {
  const [checking, setChecking] = useState(true);
  const [allow, setAllow] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;

    const checkFirebase = () => {
      // Optional whitelist from .env
      const WL = String(process.env.REACT_APP_ADMIN_WHITELIST || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      const inWhitelist = (email) =>
        !!email && WL.length > 0 && WL.includes(String(email).toLowerCase());

      const off = auth?.onIdTokenChanged?.(async (u) => {
        if (cancelled) return;
        if (!u) {
          setAllow(false);
          setChecking(false);
          return;
        }
        try {
          const t = await u.getIdTokenResult();
          const claims = t?.claims || {};
          const isAdminClaim =
            claims.admin === true ||
            String(claims.role || "").toLowerCase() === "admin";
          const allowed = isAdminClaim || inWhitelist(u.email);
          setAllow(allowed);
          setChecking(false);
        } catch (err) {
          console.warn("[ProtectedRoute] Firebase error:", err);
          setAllow(false);
          setChecking(false);
        }
      });
      return off;
    };

    // Fallback for local session (adminAuth)
    const checkLocal = () => {
      if (isAuthed() && !isExpired()) {
        setAllow(true);
      } else {
        setAllow(false);
      }
      setChecking(false);
    };

    // Pick mode
    if (auth) {
      const off = checkFirebase();
      return () => {
        cancelled = true;
        if (typeof off === "function") off();
      };
    } else {
      checkLocal();
    }

    // Listen for logout events (multi-tab safe)
    const unsub = onAuthChange(() => {
      if (!getSession()) {
        setAllow(false);
      }
    });
    return () => unsub();
  }, []);

  // Optional: force logout if expired
  useEffect(() => {
    if (isExpired()) adminLogout();
  }, [loc]);

  if (checking) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-gray-600 text-sm">
        Checking access…
      </div>
    );
  }

  if (!allow) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/admin-login?next=${next}`} replace />;
  }

  return children;
}
